'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { logout } from '@/features/auth/store/actions';
import { deveMontarPesquisaNaRota, PesquisaSatisfacaoToast } from '@/features/pesquisaSatisfacao';
import { ImpersonationProvider } from '@/features/admin-geral/contexts/ImpersonationContext';
import { ImpersonateBar } from '@/features/admin-geral/components/layout/ImpersonateBar';
import { useImpersonation } from '@/features/admin-geral/hooks/useImpersonation';
import { deveConfinarAoPortal } from '@/shared/utils/sessaoPortal';
import { ehTelaDoEnvioArbitral, isPublicPath } from '@/shared/constants/publicRoutes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { LoadingScreen } from '../ui/common/LoadingScreen';
import Layout from './Layout';
const ImpersonateBarGlobal: React.FC = () => {
  const $ = _c(7);
  const {
    isImpersonating,
    targetUserNome,
    targetTenantNome,
    exitImpersonation,
    abrirPlanoDoWorkspace
  } = useImpersonation();
  if (!isImpersonating) {
    return null;
  }
  const t0 = targetUserNome ?? "";
  const t1 = targetTenantNome ?? "";
  let t2;
  if ($[0] !== exitImpersonation) {
    t2 = () => void exitImpersonation("/admin-geral");
    $[0] = exitImpersonation;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== abrirPlanoDoWorkspace || $[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = <ImpersonateBar userNome={t0} tenantNome={t1} onExit={t2} onAbrirPlano={abrirPlanoDoWorkspace} />;
    $[2] = abrirPlanoDoWorkspace;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface LayoutWrapperProps {
  children: React.ReactNode;
}
const ehRotaOuDescendente = (pathname: string, base: string): boolean => pathname === base || pathname.startsWith(`${base}/`);
export const LayoutWrapper: React.FC<LayoutWrapperProps> = t0 => {
  const $ = _c(77);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    user,
    isAuthenticated
  } = useAppSelector(_temp);
  const [isLoading, setIsLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsMounted(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== pathname) {
    t3 = isPublicPath(pathname) || process.env.NODE_ENV === "development" && pathname === "/design-system/secretaria-permissoes";
    $[2] = pathname;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const publicPath = t3;
  const isAdminGeralPath = pathname.startsWith("/admin-geral");
  let t4;
  if ($[4] !== pathname) {
    t4 = deveMontarPesquisaNaRota(pathname);
    $[4] = pathname;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const podeMontarPesquisa = t4;
  let t5;
  if ($[6] !== pathname) {
    t5 = pathname.startsWith("/portal");
    $[6] = pathname;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const isPortalPath = t5;
  let t6;
  if ($[8] !== pathname) {
    t6 = pathname === "/arbitro" || pathname.startsWith("/arbitro/");
    $[8] = pathname;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const isArbitroWorkspacePath = t6;
  let t7;
  if ($[10] !== pathname) {
    t7 = pathname === "/advogado" || pathname.startsWith("/advogado/");
    $[10] = pathname;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const isAdvogadoWorkspacePath = t7;
  let t8;
  if ($[12] !== pathname) {
    t8 = pathname.startsWith("/compartilhado/");
    $[12] = pathname;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const isSharedItemPath = t8;
  const isScopedExternal = user?.tipoUsuario === "EXTERNO_COMPARTILHAMENTO";
  let t9;
  if ($[14] !== isPortalPath || $[15] !== pathname) {
    t9 = isPortalPath || pathname.startsWith("/login") || pathname.startsWith("/auth") || ehRotaOuDescendente(pathname, "/notificacoes/certificado") || ehRotaOuDescendente(pathname, "/termos-de-uso") || ehRotaOuDescendente(pathname, "/politica-de-privacidade") || ehTelaDoEnvioArbitral(pathname) || pathname === "/perfil";
    $[14] = isPortalPath;
    $[15] = pathname;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const isNotificadoAllowedPath = t9;
  let t10;
  if ($[17] !== user) {
    t10 = user ? deveConfinarAoPortal(user) : false;
    $[17] = user;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const deveFicarNoPortal = t10;
  let t11;
  let t12;
  if ($[19] !== isAdvogadoWorkspacePath || $[20] !== isArbitroWorkspacePath || $[21] !== isAuthenticated || $[22] !== isLoading || $[23] !== isPortalPath || $[24] !== isSharedItemPath || $[25] !== publicPath || $[26] !== router) {
    t11 = () => {
      if (isPortalPath || isSharedItemPath || isArbitroWorkspacePath || isAdvogadoWorkspacePath) {
        return;
      }
      if (!isLoading && !isAuthenticated && !publicPath) {
        router.push("/login");
      }
    };
    t12 = [isLoading, isAuthenticated, isPortalPath, isSharedItemPath, isArbitroWorkspacePath, isAdvogadoWorkspacePath, publicPath, router];
    $[19] = isAdvogadoWorkspacePath;
    $[20] = isArbitroWorkspacePath;
    $[21] = isAuthenticated;
    $[22] = isLoading;
    $[23] = isPortalPath;
    $[24] = isSharedItemPath;
    $[25] = publicPath;
    $[26] = router;
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[29] !== deveFicarNoPortal || $[30] !== isAuthenticated || $[31] !== isLoading || $[32] !== pathname || $[33] !== router || $[34] !== user) {
    t13 = () => {
      if (isLoading) {
        return;
      }
      if (!isAuthenticated || !user) {
        return;
      }
      if (pathname !== "/login") {
        return;
      }
      if (deveFicarNoPortal) {
        router.replace("/portal/dashboard");
        return;
      }
      if (user.tipoUsuario === "ADVOGADO" && user.portalAdvogadoAtivo) {
        router.replace("/advogado");
        return;
      }
      if (!user.nomeCompleto || user.nomeCompleto.trim() === "") {
        return;
      }
      router.replace("/");
    };
    t14 = [isAuthenticated, isLoading, deveFicarNoPortal, pathname, router, user];
    $[29] = deveFicarNoPortal;
    $[30] = isAuthenticated;
    $[31] = isLoading;
    $[32] = pathname;
    $[33] = router;
    $[34] = user;
    $[35] = t13;
    $[36] = t14;
  } else {
    t13 = $[35];
    t14 = $[36];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[37] !== deveFicarNoPortal || $[38] !== isAuthenticated || $[39] !== isLoading || $[40] !== isNotificadoAllowedPath || $[41] !== router || $[42] !== user) {
    t15 = () => {
      if (isLoading) {
        return;
      }
      if (!isAuthenticated || !user) {
        return;
      }
      if (!deveFicarNoPortal) {
        return;
      }
      if (!isNotificadoAllowedPath) {
        router.replace("/portal/dashboard");
      }
    };
    t16 = [isAuthenticated, isLoading, isNotificadoAllowedPath, deveFicarNoPortal, router, user];
    $[37] = deveFicarNoPortal;
    $[38] = isAuthenticated;
    $[39] = isLoading;
    $[40] = isNotificadoAllowedPath;
    $[41] = router;
    $[42] = user;
    $[43] = t15;
    $[44] = t16;
  } else {
    t15 = $[43];
    t16 = $[44];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[45] !== isAuthenticated || $[46] !== isLoading || $[47] !== pathname || $[48] !== router || $[49] !== user) {
    t17 = () => {
      if (isLoading) {
        return;
      }
      if (!isAuthenticated || !user) {
        return;
      }
      if (user.tipoUsuario !== "EXTERNO_COMPARTILHAMENTO") {
        return;
      }
      if (!user.scopedItemModule || !user.scopedItemId) {
        return;
      }
      const scopedBase = `/compartilhado/${user.scopedItemModule}/${user.scopedItemId}`;
      const isNotificacao = user.scopedItemModule === "notificacao";
      const detailBase = isNotificacao ? `/notificacoes/${user.scopedItemId}` : null;
      const isOnScopedRoute = pathname === scopedBase || pathname.startsWith(`${scopedBase}/`) || pathname.startsWith("/convite-compartilhado/") || pathname === "/compartilhado/login" || ehTelaDoEnvioArbitral(pathname) || detailBase !== null && (pathname === detailBase || pathname.startsWith(`${detailBase}/`));
      if (!isOnScopedRoute) {
        router.replace(detailBase ?? scopedBase);
      }
    };
    t18 = [isAuthenticated, isLoading, pathname, router, user];
    $[45] = isAuthenticated;
    $[46] = isLoading;
    $[47] = pathname;
    $[48] = router;
    $[49] = user;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  useEffect(t17, t18);
  const handleNotificationClick = _temp2;
  let t19;
  if ($[52] !== router) {
    t19 = () => {
      router.push("/perfil");
    };
    $[52] = router;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const handleProfileClick = t19;
  let t20;
  if ($[54] !== dispatch || $[55] !== router) {
    t20 = async () => {
      await dispatch(logout(undefined)).unwrap();
      router.replace("/login");
    };
    $[54] = dispatch;
    $[55] = router;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const handleLogout = t20;
  if (publicPath || isPortalPath || isSharedItemPath || isScopedExternal || isArbitroWorkspacePath || isAdvogadoWorkspacePath) {
    let t21;
    if ($[57] !== children) {
      t21 = <>{children}</>;
      $[57] = children;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    return t21;
  }
  if (!isMounted) {
    return null;
  }
  if (isLoading) {
    let t21;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <LoadingScreen />;
      $[59] = t21;
    } else {
      t21 = $[59];
    }
    return t21;
  }
  if (deveFicarNoPortal && !isNotificadoAllowedPath) {
    let t21;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <LoadingScreen />;
      $[60] = t21;
    } else {
      t21 = $[60];
    }
    return t21;
  }
  if (isAdminGeralPath) {
    let t21;
    if ($[61] !== children) {
      t21 = <ImpersonationProvider>{children}</ImpersonationProvider>;
      $[61] = children;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    return t21;
  }
  if (!isAuthenticated) {
    let t21;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <LoadingScreen />;
      $[63] = t21;
    } else {
      t21 = $[63];
    }
    return t21;
  }
  let t21;
  if ($[64] !== user) {
    t21 = user ? {
      id: user.id,
      email: user.email,
      nome: user.nomeCompleto,
      nomeCompleto: user.nomeCompleto,
      tipo: user.tipoUsuario.toLowerCase() as 'admin' | 'advogado' | 'assistente',
      tipoUsuario: user.tipoUsuario,
      escritorioId: user.escritorioId || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } : undefined;
    $[64] = user;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const userAdapter = t21;
  let t22;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ImpersonateBarGlobal />;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== children || $[68] !== handleLogout || $[69] !== handleProfileClick || $[70] !== userAdapter) {
    t23 = <Layout user={userAdapter} notificationCount={0} onNotificationClick={handleNotificationClick} onProfileClick={handleProfileClick} onLogout={handleLogout}>{children}</Layout>;
    $[67] = children;
    $[68] = handleLogout;
    $[69] = handleProfileClick;
    $[70] = userAdapter;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== podeMontarPesquisa) {
    t24 = podeMontarPesquisa && <PesquisaSatisfacaoToast />;
    $[72] = podeMontarPesquisa;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== t23 || $[75] !== t24) {
    t25 = <ImpersonationProvider>{t22}{t23}{t24}</ImpersonationProvider>;
    $[74] = t23;
    $[75] = t24;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  return t25;
};
function _temp(state) {
  return state.auth;
}
function _temp2() {}